import mapboxgl from 'mapbox-gl';
import { allFormats } from './coordinateFormats';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import DrawRectangle from 'mapbox-gl-draw-rectangle-mode';
import {
  CircleMode,
  DragCircleMode,
  DirectMode,
  SimpleSelectMode
} from 'mapbox-gl-draw-circle';
import * as turf from '@turf/turf';
import { formatDistanceMeters } from '../../../../utils/locationUtil';

export async function initiateComponent() {
  let lat = localStorage.getItem('lat');
  let lng = localStorage.getItem('lng');
  let zoom = localStorage.getItem('zoom');

  if (lat != null && lng != null && zoom != null) {
    // Set existing location
    const userCoordinates = [lng, lat];
    await this.props.setMapCoordinates(lng, lat, null, null, null, null, zoom);
    this.setState({
      mouseLat: lat,
      mouseLong: lng
    });
    this.setupMap(userCoordinates, zoom);
  } else {
    // Get current location
    navigator.geolocation.getCurrentPosition(
      position => {
        const userCoordinates = [
          position.coords.longitude,
          position.coords.latitude
        ];
        this.props.setMapCoordinates(
          position.coords.latitude,
          position.coords.longitude,
          null,
          null,
          null,
          null,
          13
        );
        this.setState({
          mouseLat: position.coords.latitude,
          mouseLong: position.coords.longitude
        });
        this.setupMap(userCoordinates, 13);
      },
      error => {
        this.setupMap();
      },
      {
        timeout: 5000
      }
    );
  }
}

export async function setupMap(userCoordinates = [0, 0], zoom = 2) {
  // Create map
  this.map = new mapboxgl.Map({
    container: this.mapContainer,
    style: 'mapbox://styles/jankees/cleijg9zb000t01pn0thsmz0q',
    center: userCoordinates,
    zoom: zoom,
    attributionControl: false,
    preserveDrawingBuffer: true
  });

  var pulsingRedDot = this.getPulsingDot(
    '30, 136, 229', // blue
    300,
    5000,
    this.map
  );

  var largePulsingRedDot = this.getPulsingDot(
    '30, 136, 229', // blue
    500,
    5000,
    this.map
  );

  var pulsingOrangeDot = this.getPulsingDot(
    '255, 171, 0', // yellow
    300,
    2000,
    this.map
  );

  var largePulsingOrangeDot = this.getPulsingDot(
    '255, 171, 0', // yellow
    500,
    2000,
    this.map
  );

  var pulsingGreenDot = this.getPulsingDot(
    '228, 58, 53', // red
    300,
    2000,
    this.map
  );

  var largePulsingGreenDot = this.getPulsingDot(
    '228, 58, 53', // red
    500,
    2000,
    this.map
  );
  var staticGrayDot = this.getStaticDot('189, 189, 189', 300, this.map);
  var staticGrayPin = this.getStaticPin('189, 189, 189', 500, this.map);

  // Add controls
  this.map
    .addControl(
      new mapboxgl.NavigationControl({
        showCompass: false
      })
    )
    .addControl(
      new mapboxgl.GeolocateControl({
        positionOptions: {
          enableHighAccuracy: true
        },
        fitBoundsOptions: {
          maxZoom: 10
        },
        showAccuracyCircle: false,
        showUserLocation: false
      })
    )
    .addControl(
      new mapboxgl.AttributionControl({
        compact: false
      })
    );

  // Initialize draw toolbox
  var draw = new MapboxDraw({
    displayControlsDefault: false,
    userProperties: true,
    modes: {
      ...MapboxDraw.modes,
      draw_circle: CircleMode,
      drag_circle: DragCircleMode,
      direct_select: DirectMode,
      simple_select: SimpleSelectMode,
      draw_rectangle: DrawRectangle
    },
    controls: {
      polygon: true,
      circle: true,
      trash: true
    }
  });

  let updateDrawnGeometry = e => {
    const localSelectedAreas = this.props.selectedAreas;
    if (e.type === 'draw.delete') {
      const updatedArray = localSelectedAreas.filter(
        item => item.id !== e.features[0].id
      );
      this.props.setSelectedAreas(updatedArray);
      this.props.fetchAllData();
    } else if (e.type === 'draw.create') {
      const data = draw.getAll();

      if (data.features.length > 0) {
        this.props.setSelectedAreas(data.features);

        this.props.fetchAllData();
      } else {
        if (e.type !== 'draw.delete')
          alert('Use the draw tools to draw a polygon!');
      }
    }
  };

  document.getElementById('button_circle').onclick = function() {
    draw.changeMode('drag_circle');
  };

  document.getElementById('button_rectangle').onclick = function() {
    draw.changeMode('draw_rectangle');
  };

  window.addEventListener('keydown', e => {
    if (e.key === 'Escape' && this.state.isMeasuring) {
      this.setState({ isMeasuring: false, distance: 'Measure distance' });
      this.map.getCanvas().style.cursor = '';
      this.geoJsonLine.features = [];
      this.map.getSource('geoJsonLine').setData(this.geoJsonLine);
    }
  });

  this.map.on('toggle-measure', () => {
    this.setState({ isMeasuring: !this.state.isMeasuring });
    this.map.getCanvas().style.cursor = this.state.isMeasuring
      ? 'crosshair'
      : '';
    if (!this.state.isMeasuring) {
      this.geoJsonLine.features = [];
      this.map.getSource('geoJsonLine').setData(this.geoJsonLine);
      this.setState({ distance: 'Measure distance' });
    }
  });

  // The measure-tool source/layers live on the *style*, not the map
  // instance, so calling setStyle() (basemap switch) or any runtime style
  // rebuild (e.g. Mapbox GL falling back to a full rebuild after a sprite
  // change it can't diff) silently drops them. 'load' only ever fires once,
  // so re-running this from 'style.load' too is what keeps the measure tool
  // working after the style is replaced underneath it.
  const ensureMeasureLayers = () => {
    if (!this.map.getSource('geoJsonLine')) {
      this.map.addSource('geoJsonLine', {
        type: 'geojson',
        data: this.geoJsonLine
      });
    }

    if (!this.map.getLayer('measure-points')) {
      this.map.addLayer({
        id: 'measure-points',
        type: 'circle',
        source: 'geoJsonLine',
        paint: {
          'circle-radius': 5,
          'circle-color': '#000'
        },
        filter: ['in', '$type', 'Point']
      });
    }

    if (!this.map.getLayer('measure-lines')) {
      this.map.addLayer({
        id: 'measure-lines',
        type: 'line',
        source: 'geoJsonLine',
        layout: {
          'line-cap': 'round',
          'line-join': 'round'
        },
        paint: {
          'line-color': '#000',
          'line-width': 2.5
        },
        filter: ['in', '$type', 'LineString']
      });
    }
  };

  this.map.on('load', () => {
    this.map.addControl(draw);
    this.map.addControl(new mapboxgl.ScaleControl());
    this.map.on('draw.create', updateDrawnGeometry);
    this.map.on('draw.delete', updateDrawnGeometry);
    this.map.on('draw.update', updateDrawnGeometry);
    this.map.on('draw.combine', updateDrawnGeometry);
    this.map.on('draw.uncombine', updateDrawnGeometry);

    ensureMeasureLayers();

    this.map.on('click', e => {
      if (!this.state.isMeasuring) return;

      const features = this.map.queryRenderedFeatures(e.point, {
        layers: ['measure-points']
      });

      // Remove the linestring from the group
      // so we can redraw it based on the points collection.
      if (this.geoJsonLine.features.length > 1) this.geoJsonLine.features.pop();

      // Clear the distance container to populate it with a new value.
      // distanceContainer.innerHTML = '';

      // If a feature was clicked, remove it from the map.
      if (features.length) {
        const id = features[0].properties.id;
        this.geoJsonLine.features = this.geoJsonLine.features.filter(
          point => point.properties.id !== id
        );
      } else {
        const point = {
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [e.lngLat.lng, e.lngLat.lat]
          },
          properties: {
            id: String(new Date().getTime())
          }
        };

        this.geoJsonLine.features.push(point);
      }

      if (this.geoJsonLine.features.length > 1) {
        this.lineString.geometry.coordinates = this.geoJsonLine.features.map(
          point => point.geometry.coordinates
        );

        this.geoJsonLine.features.push(this.lineString);

        // Populate the distanceContainer with total distance
        const value = document.createElement('pre');
        const distance = turf.length(this.lineString, { units: 'meters' });
        value.textContent = `Distance measured: ${formatDistanceMeters(
          distance
        )}`;
        // distanceContainer.appendChild(value);
        this.setState({ distance: value.textContent });
      } else {
        this.setState({ distance: 'Measure distance' });
      }

      this.map.getSource('geoJsonLine').setData(this.geoJsonLine);
    });

    this.map.on('mousemove', e => {
      const features = this.map.queryRenderedFeatures(e.point, {
        layers: ['measure-points']
      });
      // Change the cursor to a pointer when hovering over a point on the map.
      // Otherwise cursor is a crosshair.
      this.map.getCanvas().style.cursor = !this.state.isMeasuring
        ? ''
        : features.length
        ? 'pointer'
        : 'crosshair';
    });
  });

  this.map.on('moveend', async e => {
    this.clearObservationSpider();
    const bounds = this.getCurrentBounds();
    const currentZoom = Number(this.map.getZoom().toFixed(2));

    await this.props.setMapCoordinates(
      this.map.getCenter().lng.toFixed(4),
      this.map.getCenter().lat.toFixed(4),
      bounds.north,
      bounds.east,
      bounds.south,
      bounds.west,
      this.map.getZoom().toFixed(2)
    );

    localStorage.setItem('lng', this.map.getCenter().lng.toFixed(4));
    localStorage.setItem('lat', this.map.getCenter().lat.toFixed(4));
    localStorage.setItem('zoom', this.map.getZoom().toFixed(2));

    if (this.skipNextMoveEndFetch) {
      this.skipNextMoveEndFetch = false;
      return;
    }

    this.lastFetchedBounds = bounds;
    this.lastFetchedZoom = currentZoom;
    this.props.fetchAllData({ force: true, refreshAlerts: true });
  });

  this.map.on('zoomend', () => {
    // Recompute client-side observation merge buckets for the new zoom level.
    this.updateMapLayers();
  });

  this.map.on('mousemove', e => {
    if (this.mouseLocationRef.current) {
      this.mouseLocationRef.current.changeLocation(e);
    }
  });

  this.map.on('contextmenu', e => {
    e.preventDefault();
    const { lat, lng } = e.lngLat;
    const formats = allFormats(lat, lng);

    const container = document.createElement('div');
    container.style.cssText =
      'font-size:12px;font-family:monospace;width:max-content;padding:4px 0;margin-top:-10px';

    const header = document.createElement('div');
    header.textContent = 'Pinned position';
    header.style.cssText =
      'font-size:0.7rem;color:#888;font-family:sans-serif;margin-bottom:4px;padding-right:24px';
    container.appendChild(header);

    formats.forEach(({ key, label, value }) => {
      const row = document.createElement('div');
      row.style.cssText =
        'display:flex;align-items:center;padding:1px 0;gap:4px;white-space:nowrap';

      const lbl = document.createElement('span');
      lbl.style.minWidth = '3.8rem';
      lbl.textContent = `${label}:`;

      const val = document.createElement('span');
      val.style.cssText = 'flex:1;white-space:nowrap';
      val.textContent = value;

      const btn = document.createElement('span');
      btn.className = 'material-icons';
      btn.textContent = 'content_copy';
      btn.title = `Copy ${label}`;
      btn.style.cssText =
        'font-size:13px;cursor:pointer;opacity:0.7;user-select:none';
      btn.addEventListener('click', () => {
        navigator.clipboard.writeText(`${label}: ${value}`).then(() => {
          btn.textContent = 'check';
          setTimeout(() => (btn.textContent = 'content_copy'), 1500);
        });
      });

      row.appendChild(lbl);
      row.appendChild(val);
      row.appendChild(btn);
      container.appendChild(row);
    });

    if (this._coordinatePopup) {
      this._coordinatePopup.remove();
    }
    this._coordinatePopup = new mapboxgl.Popup({
      closeButton: true,
      maxWidth: 'none'
    })
      .setLngLat(e.lngLat)
      .setDOMContent(container)
      .addTo(this.map);
  });

  this.map.once('load', async () => {
    const bounds = this.getCurrentBounds();

    await this.props.setMapCoordinates(
      this.map.getCenter().lng.toFixed(4),
      this.map.getCenter().lat.toFixed(4),
      bounds.north,
      bounds.east,
      bounds.south,
      bounds.west,
      this.map.getZoom().toFixed(2)
    );

    this.lastFetchedBounds = bounds;
    this.lastFetchedZoom = Number(this.map.getZoom().toFixed(2));
    this.props.fetchAllData({ force: true, refreshAlerts: true });
  });

  // Add layers
  this.map.on('style.load', async () => {
    ensureMeasureLayers();

    this.map.addImage('pulsing-red-dot', pulsingRedDot, { pixelRatio: 2 });
    this.map.addImage('large-pulsing-red-dot', largePulsingRedDot, {
      pixelRatio: 2
    });
    this.map.addImage('pulsing-orange-dot', pulsingOrangeDot, {
      pixelRatio: 2
    });
    this.map.addImage('large-pulsing-orange-dot', largePulsingOrangeDot, {
      pixelRatio: 2
    });
    this.map.addImage('pulsing-green-dot', pulsingGreenDot, {
      pixelRatio: 2
    });
    this.map.addImage('large-pulsing-green-dot', largePulsingGreenDot, {
      pixelRatio: 2
    });
    this.map.addImage('static-gray-dot', staticGrayDot, {
      pixelRatio: 2
    });
    this.map.addImage('static-gray-pin', staticGrayPin, {
      pixelRatio: 2
    });

    if (this.props.mapLayers.length > 0) {
      this.whenMapLayersLengthIsLargerThan0();
    }

    this.setState({ isStyleLoaded: true });
  });
}
